/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { type Operation } from '@apollo/client';
import type { NetworkError } from '@apollo/client/errors';
import { type ErrorHandler } from '@apollo/client/link/error';
import { assert } from '@fai/util/types';
import * as Sentry from '@sentry/react';

import { SentryTags, ErrorType } from './reporters';
import { getGraphQLErrorsList, getOperationDetails } from './utils/graphql';

/**
 * @name `[GraphQL Operation]: ${operationName} failed`
 * @message `with variable of ${JSON.stringify(variables)}`
 * @stack undefined - `GraphQLError` does not have stacks
 */
export const reportFromApolloErrorLinkToSentry = ({
  errors,
  operation,
}: {
  errors: Parameters<ErrorHandler>[0]['graphQLErrors'];
  operation: Operation;
}) => {
  Sentry.withScope((scope) => {
    scope.clear();
    scope.setLevel('error');
    scope.setTag(SentryTags.ERROR_TYPE, ErrorType.GRAPHQL);

    const gqlErrors = getGraphQLErrorsList(errors);
    assert(gqlErrors, 'getGraphQLErrorsList should have returned');
    for (const [index, gqlError] of gqlErrors.entries()) {
      scope.setContext(`graphQL error-${index}`, gqlError);
    }

    const { operationName, query, variables } = getOperationDetails(operation);
    scope.setContext('operation', { operationName, query, variables });

    const errorToReport = new Error(`with variable of ${variables}`);
    const errorName = `[GraphQL Operation]: ${operationName} failed`;
    errorToReport.name = errorName;

    Sentry.captureException(errorToReport);
  });
};

/**
 * @note TODO: we need to decide what we should send to sentry about this error
 *
 * @name error?.name || GraphQL Network Error
 * @message error?.message
 * @stack error?.stack
 */
export const reportNetworkErrorToSentry = (error: NetworkError) => {
  Sentry.withScope((scope) => {
    scope.clear();
    scope.setLevel('log');
    scope.setTag(SentryTags.ERROR_TYPE, ErrorType.NETWORK);

    // TODO: fill up contexts -- e.g. the cause of failure(timeout, BE issue etc.)
    const errorToReport = new Error(error?.message);
    errorToReport.name = error?.name ?? `GraphQL Network Error`;
    errorToReport.stack = error?.stack;

    Sentry.captureException(errorToReport);
  });
};
